/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { ParsedMediaType } from 'content-type';
import { parse as parseContentType } from 'content-type';
// eslint-disable-next-line sort-imports
import { ConnectionError, HTTPError, safeGetRetryAfterMs, XHubChatError } from './error';

/**
 * Calculate the backoff time for a request retry attempt.
 * This produces wait times of 2, 4, 8, and 16 seconds (30s total) after which we give up. If the
 * failure was due to a rate limited request, the time specified in the error is returned.
 *
 * Returns -1 if the error is not retry-able, or if we reach the maximum number of attempts.
 *
 * @param err - The error thrown by the http call
 * @param attempts - The number of attempts made so far, including the one that just failed.
 * @param retryConnectionError - Whether to retry on {@link ConnectionError} (CORS, connection is down, etc.)
 */
export function calculateRetryBackoff(err: any, attempts: number, retryConnectionError: boolean): number {
  if (attempts > 4) {
    return -1; // give up
  }

  if (err instanceof ConnectionError && !retryConnectionError) {
    return -1;
  }

  if (err.httpStatus && Math.floor(err.httpStatus / 100) === 4 && err.httpStatus !== 429) {
    // client error; no amount of retrying will save you now (except for rate limiting which is handled below)
    return -1;
  }

  if (err.name === 'AbortError') {
    // this is a client timeout, that is already very high 60s/80s
    // we don't want to retry, as it could do it for very long
    return -1;
  }

  // If we are trying to send an event (or similar) that is too large in any way, then retrying won't help
  if (err.name === 'M_TOO_LARGE') {
    return -1;
  }

  return safeGetRetryAfterMs(err, 1000 * 2 ** attempts);
}

/**
 * Polyfill for Promise.withResolvers() which is not available in older environments.
 */
export function createPromiseWithResolvers<T>(): PromiseWithResolvers<T> {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: any) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

export function timeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort();
  }, ms);

  return controller.signal;
}

export function anySignal(signals: AbortSignal[]): {
  signal: AbortSignal;
  cleanup: () => void;
} {
  const controller = new AbortController();

  function cleanup(): void {
    for (const signal of signals) {
      signal.removeEventListener('abort', onAbort);
    }
  }

  function onAbort(): void {
    controller.abort();
    cleanup();
  }

  for (const signal of signals) {
    if (signal.aborted) {
      onAbort();
      break;
    }
    signal.addEventListener('abort', onAbort);
  }

  return {
    signal: controller.signal,
    cleanup,
  };
}

function isXhr(response: XMLHttpRequest | Response): response is XMLHttpRequest {
  return 'getResponseHeader' in response;
}

/**
 * extract the Content-Type header from response headers, and
 * parse it to a `{type, parameters}` object.
 *
 * returns null if no content-type header could be found.
 *
 * @param headers - response headers
 * @returns parsed content-type header, or null if not found
 */
function getResponseContentType(headers: Headers): ParsedMediaType | null {
  const contentType = headers.get('Content-Type');
  if (contentType === null) return null;

  try {
    return parseContentType(contentType);
  } catch (e) {
    throw new Error(`Error parsing Content-Type '${contentType}': ${e}`);
  }
}

/**
 * Attempt to turn an HTTP error response into a Javascript Error.
 *
 * If it is a JSON response, we will parse it into a XHubChatError. Otherwise
 * we return a generic Error.
 *
 * @param response - response object
 * @param body - raw body of the response
 * @returns The resulting Error object.
 */
export function parseErrorResponse(response: XMLHttpRequest | Response, body?: string): Error {
  const httpHeaders = isXhr(response)
    ? new Headers(
      response
        .getAllResponseHeaders()
        .trim()
        .split(/[\r\n]+/)
        .map((header): [string, string] => {
          const colonIdx = header.indexOf(':');
          return [header.substring(0, colonIdx), header.substring(colonIdx + 1)];
        }),
    )
    : response.headers;

  let contentType: ParsedMediaType | null;
  try {
    contentType = getResponseContentType(httpHeaders);
  } catch (e) {
    return <Error>e;
  }
  if (contentType?.type === 'application/json' && body) {
    return new XHubChatError(
      JSON.parse(body),
      response.status,
      isXhr(response) ? response.responseURL : response.url,
      undefined,
      httpHeaders,
    );
  }
  if (contentType?.type === 'text/plain') {
    return new HTTPError(`Server returned ${response.status} error: ${body}`, response.status, httpHeaders);
  }
  return new HTTPError(`Server returned ${response.status} error`, response.status, httpHeaders);
}
